import React, { useRef, useMemo, useState } from "react";
import { Alert } from "services/Alert";

import { getServiceSmsPrefix, toStartDayUTCTimeNextDay, toStartDayUTCTime } from "utils";
import { useWindowWidth } from "hooks";
import { api } from "api/loginRoutes";
import {
    ACCOUNT_SERVICE_PLAN_LIST_API,
    TRUNK_MODIFY_API,
    TRUNK_MODIFY_SMS_API,
    TRUNK_CREATE_API,
    TRUNK_CREATE_SMS_API
} from "const/apiMethods";
import Modal from "components/Modal";
import TrunkModalWarning from "./TrunkModalWarning";
import TrunkForm from "../TrunkForm/TrunkFormRefactored";
import { getMethodByService, hasFormErrors } from "./TrunkModal.utils";

const AUTH_MODE_NONE = "none";
const AUTH_MODE_REGISTER = "register";

export default ({
    onClose,
    service,
    trunksList,
    account_id,
    initialValues,
    ipOptionList,
    ivr_list,
    update,
    account,
    account_name,
    accountServicePlanList
}) => {
    const form = useRef(null);
    const dialerForm = useRef(null);
    const ivrForm = useRef(null);
    const externalResourceForm = useRef(null);

    const windowWidth = useWindowWidth();
    const [disabled, setDisabled] = useState(false);

    const [error, setError] = useState({});

    const [modalWarningShow, setModalWarningShow] = useState(false);
    const [moreData, setMoreData] = useState({
        payout_model: initialValues?.payout_model,
        revenue_percent: initialValues?.revenue_percent,
        postback_url: initialValues?.postback_url,
        postback_method: initialValues?.postback_method || (!service ? "GET" : undefined)
    });

    const [termPoint, setTermPoint] = useState(initialValues.termination_point || "default");

    const [moreOptionsFlag, setMoreOptionsFlag] = useState(!!initialValues?.payout_model || !!initialValues?.postback_url);
    const [externalAuthMode, setExternalAuthMode] = useState(
        initialValues?.external_require_registration
            ? AUTH_MODE_REGISTER
            : AUTH_MODE_NONE
    );

    const isDefault = termPoint === "default";
    const isDialer = termPoint === "dialer";
    const isExternal = termPoint === "external";

    const checkForm = (formRef) => {
        return !!formRef.current?.check?.();
    };

    const getFormValue = (formRef) => {
        return formRef.current?.getFormValue?.() || {};
    };

    const onPostbackTest = async () => {
        if (!checkForm(form)) return;

        const formData = getFormValue(form);
        const method = getServiceSmsPrefix(service, "trunk:test_postback");

        const postbackUrl = moreData?.postback_url || formData.postback_url;
        const postbackMethod = moreData?.postback_method || formData.postback_method || "GET";

        if (postbackUrl && postbackMethod) {
            try {
                await api(method, {
                    target: {
                        account_id,
                        trunk_id: formData.id
                    },
                    postback_url: postbackUrl,
                    postback_method: postbackMethod
                }).then((response) => {
                    if (response.postback_url) {
                        Alert.success("Postback was queued");
                    } else {
                        Alert.error("Something wrong");
                    }
                });
            } catch (e) {
                console.log("Postback test error");
            }
        }
    };

    const onSubmit = async () => {
        if (!checkForm(form)) return;

        if (isDialer && !checkForm(dialerForm)) return;
        if (isExternal && !checkForm(externalResourceForm)) return;
        if (disabled) return;

        setDisabled(true);
        const method = getMethodByService({
            service,
            modifyApi: TRUNK_MODIFY_API,
            modifySmsApi: TRUNK_MODIFY_SMS_API,
            createApi: TRUNK_CREATE_API,
            createSmsApi: TRUNK_CREATE_SMS_API,
            initialValues,
        });

        let _data = getFormValue(form);

        const todayDate = toStartDayUTCTime(new Date());
        let startDate = _data.start_date
            ? toStartDayUTCTime(new Date(_data.start_date))
            : null;

        const isToday = startDate && (
            todayDate.getFullYear() === startDate.getFullYear() &&
            todayDate.getMonth() === startDate.getMonth() &&
            todayDate.getDate() === startDate.getDate()
        );

        const data = {
            name: _data.name,
            sp_key: _data.sp_key,
            prt_key: _data.prt_key || null,
            rrc_key: _data.rrc_key,

            start_date: !initialValues.id
                ? (startDate && !isToday) ? startDate : null

                : isToday
                    ? _data.start_date ? _data.start_date : null
                    : startDate,
            end_date: _data.end_date
                ? toStartDayUTCTimeNextDay(new Date(_data.end_date))
                : null,

            active: !!_data.active,
            autorevoke: _data.autorevoke,

            ...(!service ? { full_message: !!_data.full_message } : {}),

            ...(_data.is_reseller ? {
                is_reseller: _data.is_reseller,
                subacc_rate_percent_month: _data.subacc_rate_percent_month / 100,
                subacc_rate_percent_week: _data.subacc_rate_percent_week / 100,
            } : {}),

            termination_point: termPoint,

            delivery_delay_type: _data.delivery_delay_type,
            dlr_percent: _data.dlr_percent / 100,
            bill_only_delivered: _data.bill_only_delivered,
            block_traffic_limit_excess: _data.block_traffic_limit_excess
        };

        if (isDefault && ivrForm.current) {
            const formData = getFormValue(ivrForm);
            _data.ivr_id = formData.ivr_id;
        }

        if (isDialer) {
            const formData = getFormValue(dialerForm);
            data.prt_key = formData.prt_key;
            data.ip = formData.ip;
            data.port = +formData.port;
            data.tprefix = formData.tprefix;
        }

        if (isExternal) {
            const formData = getFormValue(externalResourceForm);

            const hasExternalAuth = externalAuthMode !== AUTH_MODE_NONE;
            const normalizeString = (value) => {
                if (value === undefined || value === null) return null;

                const trimmedValue = String(value).trim();
                return trimmedValue ? trimmedValue : null;
            };
            const normalizeInteger = (value) => {
                if (value === undefined || value === null || value === "") return null;

                const parsedValue = parseInt(value, 10);
                return Number.isNaN(parsedValue) ? null : parsedValue;
            };

            data.external_fqdn = normalizeString(formData.external_fqdn);
            data.external_sip_port = normalizeInteger(formData.external_sip_port);
            data.external_tprefix_out = normalizeString(formData.external_tprefix_out) || "";
            data.external_login = normalizeString(formData.external_login);
            data.external_password = normalizeString(formData.external_password);
            data.external_realm = normalizeString(formData.external_realm);
            data.external_require_registration = hasExternalAuth;
        }

        if (!service || moreOptionsFlag) {
            const formData = moreData;
            if (isDefault) {
                if (formData.postback_url || formData.postback_url === "") data.postback_url = formData.postback_url;
            } else {
                data.postback_url = "";
            }
            if (formData.postback_method) data.postback_method = formData.postback_method;
            if (formData.payout_model) data.payout_model = parseInt(formData.payout_model);
            if (formData.revenue_percent) data.revenue_percent = parseFloat(parseFloat(formData.revenue_percent).toFixed(1));
        }

        if (data.tprefix) {
            for (let trunkData of trunksList) {
                if (data.sp_key !== trunkData.sp_key) {
                    if ((data.sp_key === 3 || trunkData.sp_key === 3) && data.tprefix === trunkData.tprefix) {
                        setDisabled(false);
                        Alert.error(`Techprefix: ${data.tprefix} is not unique "${account_name}" trunk "${trunkData.name}"`, 5000);
                        return;
                    }
                }
            }
        }

        const currentSpPlanList = await api(ACCOUNT_SERVICE_PLAN_LIST_API, { target: { account_id: account_id } })
            .then(({ account_service_plan_list }) => account_service_plan_list);
        const currentSpPlan = currentSpPlanList.find(item => item.sp_key === data.sp_key);

        if ((currentSpPlan && !currentSpPlan.allowed) && data.active) {
            setModalWarningShow(true);
            setDisabled(false);
            return;
        }

        let isChanged = false;
        try {
            await api(method, {
                target: {
                    account_id,
                    ...(service ? { trunk_id: _data.id } : { "sms.trunk_id": _data.id }),
                    ...(Object.keys(_data).includes("ivr_id") ? { ivr_id: _data.ivr_id } : {})
                },
                ...data,
                sp_key: data.sp_key
            }).finally(() => {
                onClose();

                if (!isChanged) {
                    update();
                }
            });

        } catch (e) {
            console.log("Change trunk error");
        }

        setDisabled(false);
    };

    const modalWidth = 820;

    const isFormHasError = useMemo(() => {
        return hasFormErrors(error);
    }, [error]);

    return (
        <>
            <Modal
                onClose={onClose}
                show={true}
                onSuccess={onSubmit}
                disabled={disabled}
                extraDisabled={isFormHasError}
                title="Create/Edit Trunk"
                footer
                successText="Save"
                width={windowWidth >= modalWidth ? modalWidth : windowWidth}
            >
                <TrunkForm
                    accountServicePlanList={accountServicePlanList}
                    account={account}
                    service={service}
                    ivr_list={ivr_list}
                    disabled={disabled}
                    termPoint={termPoint}
                    isDefault={isDefault}
                    setDisabled={setDisabled}
                    updateFormRef={form}
                    updateFormRef2={dialerForm}
                    updateFormRef3={ivrForm}
                    updateFormRef4={externalResourceForm}
                    setMoreData={setMoreData}
                    setTermPoint={setTermPoint}
                    externalAuthMode={externalAuthMode}
                    setExternalAuthMode={setExternalAuthMode}
                    initialValues={initialValues}
                    newTrunk={!initialValues.id}
                    isMobile={windowWidth < 620}
                    error={error}
                    setError={setError}
                    ipOptionList={ipOptionList}
                    onPostbackTest={onPostbackTest}
                    moreOptionsFlag={moreOptionsFlag}
                    setMoreOptionsFlag={setMoreOptionsFlag}
                />
            </Modal>

            <TrunkModalWarning
                show={modalWarningShow}
                onClose={() => setModalWarningShow(false)}
            />
        </>
    );
};
